import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authApi, shopifyApi } from '@/services/api';
import {
  ShoppingBag, Save, Loader2, CheckCircle, AlertTriangle, RefreshCw,
  Eye, EyeOff, Copy, Link2, Radio, Package, Warehouse, Download,
  ChevronDown, ChevronUp, ExternalLink, Sparkles, BookOpen, Unplug,
} from 'lucide-react';

type LocationOpt = { id: string; name: string; is_active?: boolean };

export default function ShopifyIntegrationTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showToken, setShowToken] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [actionMsg, setActionMsg] = useState<{ success: boolean; message: string } | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [copied, setCopied] = useState('');
  const [locations, setLocations] = useState<LocationOpt[]>([]);
  const [oauthShop, setOauthShop] = useState('');

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await authApi.getCompanyProfile();
      return res.data.message;
    },
  });

  const packageAllows = Boolean(profile?.allow_shopify);

  const { data: status, refetch: refetchStatus } = useQuery({
    queryKey: ['shopifyStatus'],
    queryFn: async () => {
      const res = await shopifyApi.getStatus();
      return res.data.message as {
        package_allows: boolean;
        enable_shopify: boolean;
        shopify_shop_domain?: string;
        shopify_location_id?: string;
        shopify_sync_products_mode?: string;
        shopify_auto_confirm_orders?: boolean;
        shopify_connection_status?: string;
        has_access_token?: boolean;
        has_webhook_secret?: boolean;
        shopify_last_product_sync_at?: string;
        shopify_last_inventory_sync_at?: string;
        shopify_last_order_sync_at?: string;
        marked_products?: number;
        linked_products?: number;
        pending_orders?: number;
        webhook_url?: string;
        recent_logs?: Array<{
          name: string;
          direction: string;
          entity: string;
          status: string;
          message: string;
          creation: string;
        }>;
        is_demo?: boolean;
        oauth_installed?: boolean;
        oauth_app_configured?: boolean;
      };
    },
    enabled: packageAllows || Boolean(profile?.enable_shopify),
  });

  useEffect(() => {
    const flag = searchParams.get('shopify');
    if (!flag) return;
    if (flag === 'connected') {
      toast.success(t('shopify.oauthConnected'));
      void refetchStatus();
      qc.invalidateQueries({ queryKey: ['profile'] });
    } else if (flag === 'error') {
      toast.error(t('shopify.oauthFailed', { reason: searchParams.get('reason') || '' }));
    }
    const next = new URLSearchParams(searchParams);
    next.delete('shopify');
    next.delete('reason');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form, setForm] = useState({
    enable_shopify: false,
    shopify_shop_domain: '',
    shopify_client_id: '',
    shopify_client_secret: '',
    shopify_access_token: '',
    shopify_webhook_secret: '',
    shopify_api_version: '2025-01',
    shopify_location_id: '',
    shopify_sync_products_mode: 'Selected',
    shopify_auto_confirm_orders: false,
    shopify_auto_fetch_orders: false,
  });

  useEffect(() => {
    if (profile) {
      setForm({
        enable_shopify: profile.enable_shopify === 1 || profile.enable_shopify === true,
        shopify_shop_domain: profile.shopify_shop_domain || '',
        shopify_client_id: profile.shopify_client_id || '',
        shopify_client_secret: '',
        shopify_access_token: '',
        shopify_webhook_secret: '',
        shopify_api_version: profile.shopify_api_version || '2025-01',
        shopify_location_id: profile.shopify_location_id || '',
        shopify_sync_products_mode: profile.shopify_sync_products_mode || 'Selected',
        shopify_auto_confirm_orders:
          profile.shopify_auto_confirm_orders === 1 || profile.shopify_auto_confirm_orders === true,
        shopify_auto_fetch_orders:
          profile.shopify_auto_fetch_orders === 1 || profile.shopify_auto_fetch_orders === true,
      });
    }
  }, [profile]);

  const saveMutation = useMutation({
    mutationFn: (data: typeof form) => authApi.updateCompanyProfile(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['shopifyStatus'] });
      setSaveSuccess(true);
      setSaveError('');
      setTimeout(() => setSaveSuccess(false), 4000);
    },
    onError: (err: any) => {
      setSaveError(err.response?.data?.message || err.message || t('shopify.saveFailed'));
    },
  });

  const testMutation = useMutation({
    mutationFn: () => shopifyApi.testConnection(),
    onSuccess: (res) => {
      const msg = res.data.message;
      const locs: LocationOpt[] = msg?.locations || [];
      setLocations(locs);
      if (locs.length && !form.shopify_location_id) {
        setForm((f) => ({ ...f, shopify_location_id: locs[0].id }));
      }
      setTestResult({
        success: true,
        message: msg?.demo
          ? t('shopify.demoConnected', { name: msg?.shop_name || 'Demo' })
          : t('shopify.connectionOk', { name: msg?.shop_name || '' }),
      });
      void refetchStatus();
    },
    onError: (err: any) => {
      setTestResult({
        success: false,
        message: err.response?.data?.message || err.message || t('shopify.connectionFailed'),
      });
    },
  });

  const runAction = (
    fn: () => Promise<any>,
    okMsg: (msg: any) => string,
  ) => {
    setActionMsg(null);
    return fn()
      .then((res) => {
        setActionMsg({ success: true, message: okMsg(res.data.message) });
        void refetchStatus();
        qc.invalidateQueries({ queryKey: ['profile'] });
      })
      .catch((err: any) => {
        setActionMsg({
          success: false,
          message: err.response?.data?.message || err.message || t('shopify.actionFailed'),
        });
      });
  };

  const syncProductsMutation = useMutation({
    mutationFn: () =>
      runAction(
        () => shopifyApi.syncProducts(),
        (m) =>
          m?.queued
            ? (m?.message || t('shopify.syncProductsQueued', { count: m?.total ?? 0 }))
            : t('shopify.syncProductsOk', { count: m?.synced ?? 0 }),
      ),
  });
  const syncInventoryMutation = useMutation({
    mutationFn: () =>
      runAction(
        () => shopifyApi.syncInventory(),
        (m) =>
          (m?.skipped ?? 0) > 0
            ? t('shopify.syncInventoryPartial', {
                count: m?.synced ?? 0,
                skipped: m?.skipped ?? 0,
              })
            : t('shopify.syncInventoryOk', { count: m?.synced ?? 0 }),
      ),
  });
  const fetchOrdersMutation = useMutation({
    mutationFn: () =>
      runAction(
        () => shopifyApi.fetchOrders(25),
        (m) => t('shopify.fetchOrdersOk', { count: m?.fetched ?? 0 }),
      ),
  });
  const oauthConnectMutation = useMutation({
    mutationFn: () => shopifyApi.getOauthUrl(oauthShop || form.shopify_shop_domain || undefined),
    onSuccess: (res) => {
      const url = res.data.message?.authorize_url;
      if (url) window.location.href = url;
      else toast.error(t('shopify.oauthFailed', { reason: 'no_url' }));
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || t('shopify.oauthFailed', { reason: '' }));
    },
  });

  const oauthDisconnectMutation = useMutation({
    mutationFn: () => shopifyApi.disconnectOauth(),
    onSuccess: () => {
      toast.success(t('shopify.oauthDisconnected'));
      void refetchStatus();
      qc.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || t('shopify.actionFailed'));
    },
  });

  const demoMutation = useMutation({
    mutationFn: () => shopifyApi.seedDemo(),
    onSuccess: (res) => {
      const msg = res.data.message;
      setActionMsg({
        success: true,
        message: msg?.message || t('shopify.demoReady'),
      });
      qc.invalidateQueries({ queryKey: ['profile'] });
      qc.invalidateQueries({ queryKey: ['shopifyStatus'] });
      setForm((f) => ({
        ...f,
        enable_shopify: true,
        shopify_shop_domain: 'demo.myshopify.com',
        shopify_location_id: 'gid://shopify/Location/1001',
      }));
      setLocations([
        { id: 'gid://shopify/Location/1001', name: 'Main Warehouse', is_active: true },
        { id: 'gid://shopify/Location/1002', name: 'Front Shop', is_active: true },
      ]);
    },
    onError: (err: any) => {
      setActionMsg({
        success: false,
        message: err.response?.data?.message || err.message || t('shopify.demoFailed'),
      });
    },
  });

  const copy = (label: string, value: string) => {
    if (!value) return;
    void navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const statusBadge = useMemo(() => {
    const s = status?.shopify_connection_status || profile?.shopify_connection_status || 'Disconnected';
    if (s === 'Connected') return 'bg-emerald-100 text-emerald-800';
    if (s === 'Error') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  }, [status, profile]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!packageAllows && !profile?.enable_shopify) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-50 text-green-700 flex items-center justify-center shrink-0">
            <ShoppingBag className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('shopify.title')}</h2>
            <p className="text-sm text-gray-500">{t('shopify.subtitle')}</p>
          </div>
        </div>
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
          <p className="font-medium mb-1">{t('shopify.packageRequired')}</p>
          <p className="text-amber-800/90">{t('shopify.packageRequiredHelp')}</p>
        </div>
        <button
          type="button"
          onClick={() => demoMutation.mutate()}
          disabled={demoMutation.isPending}
          className="btn-secondary flex items-center gap-2"
        >
          {demoMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {t('shopify.tryDemo')}
        </button>
        {actionMsg && (
          <div className={`p-3 rounded-lg text-sm ${actionMsg.success ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>
            {actionMsg.message}
          </div>
        )}
      </div>
    );
  }

  const webhookUrl = status?.webhook_url || '';
  const scopes = [
    'read_products', 'write_products',
    'read_inventory', 'write_inventory',
    'read_locations', 'write_locations',
    'read_orders', 'write_orders',
    'read_customers', 'write_customers',
  ];
  const scopesCsv = scopes.join(',');

  return (
    <div className="space-y-6 min-w-0 w-full max-w-full overflow-x-hidden">
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-10 h-10 rounded-lg bg-green-50 text-green-700 flex items-center justify-center shrink-0">
          <ShoppingBag className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">{t('shopify.title')}</h2>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge}`}>
              {status?.shopify_connection_status || profile?.shopify_connection_status || 'Disconnected'}
            </span>
            {(status?.is_demo || form.shopify_shop_domain?.includes('demo.myshopify')) && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                {t('shopify.demoBadge')}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 break-words">{t('shopify.subtitle')}</p>
        </div>
      </div>

      {status?.oauth_app_configured && (
        <div className="rounded-xl border border-green-200 bg-green-50/50 p-4 space-y-3 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-gray-900">{t('shopify.oauthTitle')}</p>
              <p className="text-xs text-gray-600 mt-0.5">{t('shopify.oauthHelp')}</p>
            </div>
            {status?.oauth_installed && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                {t('shopify.oauthInstalled')}
              </span>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 min-w-0">
            <input
              className="input-field flex-1 min-w-0"
              placeholder="mystore.myshopify.com"
              value={oauthShop || form.shopify_shop_domain}
              onChange={(e) => setOauthShop(e.target.value)}
            />
            <button
              type="button"
              className="btn-primary inline-flex items-center justify-center gap-2 shrink-0"
              disabled={oauthConnectMutation.isPending}
              onClick={() => oauthConnectMutation.mutate()}
            >
              {oauthConnectMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
              {t('shopify.oauthConnect')}
            </button>
            {status?.oauth_installed && (
              <button
                type="button"
                className="btn-secondary inline-flex items-center justify-center gap-2 shrink-0"
                disabled={oauthDisconnectMutation.isPending}
                onClick={() => oauthDisconnectMutation.mutate()}
              >
                {oauthDisconnectMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unplug className="w-4 h-4" />}
                {t('shopify.oauthDisconnect')}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Link
          to="/shopify-import"
          className="btn-secondary inline-flex items-center gap-2 text-sm"
        >
          <Download className="w-4 h-4" />
          {t('shopify.importCatalogLink')}
        </Link>
        <Link
          to="/shopify-orders"
          className="btn-secondary inline-flex items-center gap-2 text-sm"
        >
          <ExternalLink className="w-4 h-4" />
          {t('shopify.viewOrders')}
        </Link>
      </div>

      {/* Setup guide */}
      <div className="rounded-xl border border-green-200 bg-gradient-to-br from-green-50/80 to-white overflow-hidden min-w-0">
        <button
          type="button"
          onClick={() => setGuideOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left min-w-0"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-green-900 min-w-0">
            <BookOpen className="w-4 h-4 shrink-0" />
            <span className="truncate">{t('shopify.guideTitle')}</span>
          </span>
          {guideOpen ? <ChevronUp className="w-4 h-4 text-green-700 shrink-0" /> : <ChevronDown className="w-4 h-4 text-green-700 shrink-0" />}
        </button>
        {guideOpen && (
          <div className="px-4 pb-4 space-y-4 text-sm text-gray-700 min-w-0 overflow-x-hidden">
            <p className="text-gray-600 break-words">{t('shopify.guideIntro')}</p>
            <div className="rounded-lg border border-green-200 bg-white px-3 py-2.5 text-xs text-green-900 min-w-0">
              <p className="font-semibold mb-1">Path to the form</p>
              <p className="font-mono text-[12px] tracking-tight leading-relaxed break-words whitespace-normal">
                Settings → <span className="font-bold underline decoration-green-600/40">Apps</span>
                {' '}→ Develop apps → Build apps in Dev Dashboard → <span className="font-bold">Create an app</span>
              </p>
              <p className="mt-1.5 text-gray-600 break-words">{t('shopify.guideMenuPath')}</p>
            </div>

            <p className="text-sm font-semibold text-gray-900">{t('shopify.guideFormTitle')}</p>
            <ol className="space-y-3 list-decimal list-inside">
              <li>
                <span className="font-medium text-gray-900">{t('shopify.guideStep1Title')}</span>
                <p className="mt-1 ml-5 text-gray-600">{t('shopify.guideStep1Body')}</p>
              </li>
              <li>
                <span className="font-medium text-gray-900">{t('shopify.guideStep2Title')}</span>
                <p className="mt-1 ml-5 text-gray-600">{t('shopify.guideStep2Body')}</p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p><span className="text-gray-500">App name:</span> <code className="font-semibold text-gray-900">SaleDesk POS</code></p>
                  <p><span className="text-gray-500">Version name:</span> <span className="text-gray-600">(leave empty)</span></p>
                </div>
              </li>
              <li>
                <span className="font-medium text-gray-900">{t('shopify.guideStep3Title')}</span>
                <p className="mt-1 ml-5 text-gray-600">{t('shopify.guideStep3Body')}</p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p><span className="text-gray-500">App URL:</span> <code className="break-all">https://shopify.dev/apps/default-app-home</code></p>
                  <p><span className="text-gray-500">Embed app in Shopify admin:</span> <span className="font-medium">unchecked</span></p>
                </div>
              </li>
              <li>
                <span className="font-medium text-gray-900">{t('shopify.guideStep4Title')}</span>
                <p className="mt-1 ml-5 text-gray-600">{t('shopify.guideStep4Body')}</p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p><span className="text-gray-500">Allowed redirection URL(s):</span> <code className="break-all">https://devpos.saledesk.pk/</code> <span className="text-gray-500">(or your POS URL)</span></p>
                  <p><span className="text-gray-500">Use legacy install flow:</span> <span className="font-medium">unchecked</span></p>
                </div>
                <div className="mt-2 ml-5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-950 flex gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{t('shopify.guideRedirectWarning')}</span>
                </div>
              </li>
              <li>
                <span className="font-medium text-gray-900">{t('shopify.guideStep5Title')}</span>
                <p className="mt-1 ml-5 text-gray-600">{t('shopify.guideStep5Body')}</p>
                <p className="mt-1 ml-5 text-xs text-gray-500">{t('shopify.tipScopesSearch')}</p>
                <div className="mt-2 ml-5 flex flex-wrap gap-1.5">
                  {scopes.map((s) => (
                    <code key={s} className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-green-100 text-green-900">
                      {s}
                    </code>
                  ))}
                </div>
                <div className="mt-2 ml-5 flex flex-col sm:flex-row gap-2 min-w-0">
                  <code className="min-w-0 flex-1 text-[11px] bg-white border border-green-100 rounded px-2 py-1.5 break-all whitespace-pre-wrap">
                    {scopesCsv}
                  </code>
                  <button type="button" onClick={() => copy('scopes', scopesCsv)} className="btn-secondary px-2 py-1.5 text-xs inline-flex items-center justify-center gap-1 shrink-0">
                    <Copy className="w-3.5 h-3.5" /> {t('shopify.guideCopyScopes')}
                  </button>
                </div>
                {copied === 'scopes' && <p className="ml-5 text-xs text-emerald-600 mt-1">{t('shopify.copied')}</p>}
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs">
                  <p><span className="text-gray-500">Webhooks API version:</span> newest available (e.g. <code>2024-10</code> or later)</p>
                </div>
              </li>
              <li>
                <span className="font-medium text-gray-900">{t('shopify.guideStep6Title')}</span>
                <p className="mt-1 ml-5 text-gray-600">{t('shopify.guideStep6Body')}</p>
              </li>
              <li>
                <span className="font-medium text-gray-900">{t('shopify.guideStep7Title')}</span>
                <p className="mt-1 ml-5 text-gray-600">{t('shopify.guideStep7Body')}</p>
                <div className="mt-2 ml-5 rounded-lg border border-emerald-200 bg-emerald-50/80 p-3 text-xs text-emerald-950 space-y-2">
                  <p className="font-semibold">{t('shopify.guideCredsAlreadyInstalled')}</p>
                  <ol className="list-decimal list-inside space-y-1.5 text-gray-800">
                    <li>{t('shopify.guideCredsClick1')}</li>
                    <li>{t('shopify.guideCredsClick2')}</li>
                    <li>{t('shopify.guideCredsClick3')}</li>
                    <li>{t('shopify.guideCredsClick4')}</li>
                    <li>{t('shopify.guideCredsClick5')}</li>
                  </ol>
                  <p className="text-gray-700">{t('shopify.guideCredsShopDomain')}</p>
                  <p className="font-medium text-gray-900">{t('shopify.guideCredsPastePos')}</p>
                  <p className="text-gray-600 border-t border-emerald-200/80 pt-2">{t('shopify.guideCredsIfMissingInstall')}</p>
                </div>
              </li>
              <li>
                <span className="font-medium text-gray-900">{t('shopify.guideStep8Title')}</span>
                <p className="mt-1 ml-5 text-gray-600">{t('shopify.guideStep8Body')}</p>
                <div className="mt-2 ml-5 flex flex-col sm:flex-row gap-2 items-stretch min-w-0">
                  <code className="min-w-0 flex-1 text-xs bg-white border border-green-100 rounded px-2 py-1.5 break-all whitespace-pre-wrap">
                    {webhookUrl || t('shopify.webhookAfterSave')}
                  </code>
                  <button type="button" onClick={() => copy('webhook', webhookUrl)} className="btn-secondary px-2 py-1.5 shrink-0 self-start" disabled={!webhookUrl}>
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                {copied === 'webhook' && <p className="ml-5 text-xs text-emerald-600 mt-1">{t('shopify.copied')}</p>}
                <p className="mt-2 ml-5 text-xs text-gray-500">{t('shopify.webhookTopics')}</p>
              </li>
            </ol>
            <div className="rounded-lg bg-amber-50/80 border border-amber-100 p-3 text-xs text-amber-950">
              {t('shopify.guideLegacyNote')}
            </div>
            <div className="rounded-lg bg-white/80 border border-green-100 p-3 text-xs text-gray-600 space-y-1">
              <p className="font-medium text-gray-800">{t('shopify.tipsTitle')}</p>
              <p>• {t('shopify.tipStockMaster')}</p>
              <p>• {t('shopify.tipSku')}</p>
              <p>• {t('shopify.tipOrg')}</p>
              <p>• {t('shopify.tipDemo')}</p>
            </div>
          </div>
        )}
      </div>

      {saveSuccess && (
        <div className="p-3 bg-emerald-50 text-emerald-800 rounded-lg text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4" /> {t('shopify.saved')}
        </div>
      )}
      {saveError && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {saveError}
        </div>
      )}

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="w-5 h-5 rounded accent-green-600"
          checked={form.enable_shopify}
          onChange={(e) => setForm({ ...form, enable_shopify: e.target.checked })}
        />
        <span className="text-sm font-medium text-gray-900">{t('shopify.enable')}</span>
      </label>

      {form.enable_shopify && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('shopify.shopDomain')}</label>
              <input
                className="input-field w-full"
                value={form.shopify_shop_domain}
                onChange={(e) => setForm({ ...form, shopify_shop_domain: e.target.value })}
                placeholder="mystore.myshopify.com"
              />
              <p className="text-xs text-gray-500 mt-1 break-words">{t('shopify.shopDomainHint')}</p>
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('shopify.clientId')}</label>
              <input
                className="input-field w-full"
                value={form.shopify_client_id}
                onChange={(e) => setForm({ ...form, shopify_client_id: e.target.value })}
                placeholder="e.g. 1a2b3c…"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 mt-1 break-words">{t('shopify.clientIdHint')}</p>
            </div>

            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('shopify.clientSecret')}</label>
              <div className="relative">
                <input
                  type={showClientSecret ? 'text' : 'password'}
                  className="input-field w-full pr-10"
                  value={form.shopify_client_secret}
                  onChange={(e) => setForm({ ...form, shopify_client_secret: e.target.value })}
                  placeholder={profile?.has_shopify_client_secret ? '••••••••' : ''}
                  autoComplete="new-password"
                />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowClientSecret((v) => !v)}>
                  {showClientSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1 break-words">{t('shopify.clientSecretHint')}</p>
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('shopify.webhookSecret')}</label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  className="input-field w-full pr-10"
                  value={form.shopify_webhook_secret}
                  onChange={(e) => setForm({ ...form, shopify_webhook_secret: e.target.value })}
                  placeholder={profile?.has_shopify_webhook_secret ? '••••••••' : t('shopify.webhookSecretHint')}
                  autoComplete="new-password"
                />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowSecret((v) => !v)}>
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1 break-words">{t('shopify.webhookSecretHint')}</p>
            </div>

            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('shopify.location')}</label>
              {locations.length > 0 ? (
                <select
                  className="input-field w-full"
                  value={form.shopify_location_id}
                  onChange={(e) => setForm({ ...form, shopify_location_id: e.target.value })}
                >
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  className="input-field w-full"
                  value={form.shopify_location_id}
                  onChange={(e) => setForm({ ...form, shopify_location_id: e.target.value })}
                  placeholder={t('shopify.locationHint')}
                />
              )}
              <p className="text-xs text-gray-500 mt-1 break-words">{t('shopify.locationHint')}</p>
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('shopify.syncMode')}</label>
              <select
                className="input-field w-full"
                value={form.shopify_sync_products_mode}
                onChange={(e) => setForm({ ...form, shopify_sync_products_mode: e.target.value })}
              >
                <option value="Selected">{t('shopify.syncModeSelected')}</option>
                <option value="All active">{t('shopify.syncModeAll')}</option>
                <option value="Manual">{t('shopify.syncModeManual')}</option>
              </select>
            </div>

            <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-3 min-w-0">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 rounded accent-green-600 shrink-0"
                checked={form.shopify_auto_fetch_orders}
                onChange={(e) => setForm({ ...form, shopify_auto_fetch_orders: e.target.checked })}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900">{t('shopify.autoFetch')}</span>
                <span className="block text-xs text-gray-500 mt-0.5 break-words">{t('shopify.autoFetchHelp')}</span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-3 min-w-0">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 rounded accent-green-600 shrink-0"
                checked={form.shopify_auto_confirm_orders}
                onChange={(e) => setForm({ ...form, shopify_auto_confirm_orders: e.target.checked })}
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900">{t('shopify.autoConfirm')}</span>
                <span className="block text-xs text-gray-500 mt-0.5 break-words">{t('shopify.autoConfirmHelp')}</span>
              </span>
            </label>

            <details className="md:col-span-2 rounded-lg border border-gray-200 bg-white min-w-0">
              <summary className="cursor-pointer px-3 py-2.5 text-sm font-medium text-gray-700 select-none">
                {t('shopify.advancedOptions', { defaultValue: 'Advanced options' })}
              </summary>
              <div className="px-3 pb-3 pt-3 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('shopify.apiVersion')}</label>
                  <input
                    className="input-field w-full"
                    value={form.shopify_api_version}
                    onChange={(e) => setForm({ ...form, shopify_api_version: e.target.value })}
                    placeholder="2025-01"
                  />
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('shopify.legacyToken')}</label>
                  <div className="relative">
                    <input
                      type={showToken ? 'text' : 'password'}
                      className="input-field w-full pr-10"
                      value={form.shopify_access_token}
                      onChange={(e) => setForm({ ...form, shopify_access_token: e.target.value })}
                      placeholder={profile?.has_shopify_token ? '••••••••' : 'shpat_…'}
                      autoComplete="new-password"
                    />
                    <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowToken((v) => !v)}>
                      {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 break-words">{t('shopify.legacyTokenHint')}</p>
                </div>
              </div>
            </details>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2 min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
              <Link2 className="w-4 h-4 shrink-0" /> {t('shopify.webhookUrl')}
            </div>
            <div className="flex gap-2 min-w-0">
              <code className="flex-1 min-w-0 text-xs bg-white border border-gray-200 rounded px-3 py-2 break-all">
                {webhookUrl}
              </code>
              <button type="button" onClick={() => copy('url', webhookUrl)} className="btn-secondary px-3 shrink-0">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {copied === 'url' && <p className="text-xs text-emerald-600">{t('shopify.copied')}</p>}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm min-w-0">
            <div className="rounded-lg border border-gray-200 p-3 min-w-0">
              <p className="text-xs text-gray-500 break-words">{t('shopify.markedProducts')}</p>
              <p className="text-lg font-semibold text-gray-900 tabular-nums">{status?.marked_products ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-3 min-w-0">
              <p className="text-xs text-gray-500 break-words">{t('shopify.linkedProducts')}</p>
              <p className="text-lg font-semibold text-gray-900 tabular-nums">{status?.linked_products ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-3 min-w-0">
              <p className="text-xs text-gray-500 break-words">{t('shopify.pendingOrders')}</p>
              <p className="text-lg font-semibold text-gray-900 tabular-nums">{status?.pending_orders ?? 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-3 min-w-0 flex items-center">
              <Link to="/shopify-orders" className="text-sm font-medium text-primary-600 hover:underline inline-flex items-center gap-1 break-words">
                {t('shopify.viewOrders')} <ExternalLink className="w-3.5 h-3.5 shrink-0" />
              </Link>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <button type="button" onClick={() => testMutation.mutate()} disabled={testMutation.isPending} className="btn-secondary flex items-center gap-2">
              {testMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />}
              {t('shopify.testConnection')}
            </button>
            <button type="button" onClick={() => syncProductsMutation.mutate()} disabled={syncProductsMutation.isPending} className="btn-secondary flex items-center gap-2">
              {syncProductsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
              {t('shopify.syncProducts')}
            </button>
            <button
              type="button"
              onClick={() => syncInventoryMutation.mutate()}
              disabled={syncInventoryMutation.isPending}
              title={t('shopify.syncInventoryHelp')}
              className="btn-secondary flex items-center gap-2"
            >
              {syncInventoryMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Warehouse className="w-4 h-4" />}
              {t('shopify.syncInventory')}
            </button>
            <button type="button" onClick={() => fetchOrdersMutation.mutate()} disabled={fetchOrdersMutation.isPending} className="btn-secondary flex items-center gap-2">
              {fetchOrdersMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {t('shopify.fetchOrders')}
            </button>
            <button type="button" onClick={() => demoMutation.mutate()} disabled={demoMutation.isPending} className="btn-secondary flex items-center gap-2">
              {demoMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {t('shopify.tryDemo')}
            </button>
          </div>

          {testResult && (
            <div className={`p-3 rounded-lg text-sm ${testResult.success ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>
              {testResult.message}
            </div>
          )}
          {actionMsg && (
            <div className={`p-3 rounded-lg text-sm ${actionMsg.success ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>
              {actionMsg.message}
            </div>
          )}

          {(status?.shopify_last_product_sync_at || status?.shopify_last_inventory_sync_at || status?.shopify_last_order_sync_at) && (
            <div className="text-xs text-gray-500 space-y-0.5">
              {status.shopify_last_product_sync_at && (
                <p>{t('shopify.lastProductSync')}: {String(status.shopify_last_product_sync_at)}</p>
              )}
              {status.shopify_last_inventory_sync_at && (
                <p>{t('shopify.lastInventorySync')}: {String(status.shopify_last_inventory_sync_at)}</p>
              )}
              {status.shopify_last_order_sync_at && (
                <p>{t('shopify.lastOrderSync')}: {String(status.shopify_last_order_sync_at)}</p>
              )}
            </div>
          )}

          {!!status?.recent_logs?.length && (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" /> {t('shopify.recentLogs')}
              </div>
              <ul className="divide-y divide-gray-100 text-xs">
                {status.recent_logs.map((log) => (
                  <li key={log.name} className="px-3 py-2 flex gap-2">
                    <span className={`shrink-0 px-1.5 py-0.5 rounded ${log.status === 'Success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                      {log.status}
                    </span>
                    <span className="text-gray-500 shrink-0">{log.entity}</span>
                    <span className="text-gray-800 truncate">{log.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <div className="pt-2">
        <button
          type="button"
          onClick={() => saveMutation.mutate(form)}
          disabled={saveMutation.isPending}
          className="btn-primary flex items-center gap-2"
        >
          {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}
